import { AnvaQuote } from '@flows/src/types/anva';
import { Package } from '@flows/src/store/zustand/flows-products/useAnvaFlowProductsBasketStore';
import { isMaatwerkPackage } from './basketCalculations';

interface CalculationPayload {
  chosen_insurance_packages: Array<{
    name: string;
    code: string;
    chosen_insurance_policies: Array<{
      anva_product_id: string;
      index: number;
      coverages: number[];
    }>;
    discount_percentage?: number;
    discount?: number;
    total_premium?: number;
    payment_interval?: string;
  }>;
  chosen_insurance_policies: Array<{
    anva_product_id: string;
    index: number;
    coverages: number[];
    due_date?: string;
    payment_interval?: string;
  }>;
}

/**
 * Builds the calculation payload for basket validation
 * Filters out maatwerk packages and handles package vs standalone insurance logic
 * @param packages The packages in the basket
 * @param insurances The standalone insurances in the basket
 * @returns The calculation payload for the API
 */
export const buildCalculationPayload = (
  packages: Package[],
  insurances: AnvaQuote[],
): CalculationPayload => {
  // Filter out maatwerk packages from recalculation
  const nonMaatwerkPackages = packages.filter((pkg) => !isMaatwerkPackage(pkg));

  // Build package data
  const chosen_insurance_packages = nonMaatwerkPackages.map((pkg) => ({
    ...pkg,
    name: pkg.name,
    code: pkg.code,
    chosen_insurance_policies: pkg.quotes.map((quote) => ({
      anva_product_id: quote.anva_product_id,
      index: quote.index,
      coverages: quote.coverages
        .filter(({ selected }) => selected)
        .map(({ code }) => Number(code)),
    })),
  }));

  // Build policies data - only include standalone insurances when no packages exist
  const chosen_insurance_policies = insurances.map((quote) => ({
    anva_product_id: quote.anva_product_id,
    index: quote.index,
    coverages: quote.coverages
      .filter(({ selected }) => selected)
      .map(({ code }) => Number(code)),
    due_date: quote.start_date,
    payment_interval: quote.payment_interval ?? undefined,
  }));

  return {
    chosen_insurance_packages,
    chosen_insurance_policies,
  };
};
